import { supabase } from '../lib/supabase';
import { 
  APIConnectionError,
  AuthenticationError,
  PermissionDeniedError,
  RateLimitError,
  InternalServerError
} from 'openai';
import { analyzeDocumentQuality, calculateEnhancedOCRConfidence } from './documentQualityService';

// Rate limiting configuration
const RATE_LIMIT_CONFIG = {
  maxRetries: 3,
  baseDelay: 2000, // 2 seconds
  maxDelay: 10000, // 10 seconds
  backoffMultiplier: 2
};

// Request queue to prevent concurrent API calls
let requestQueue = Promise.resolve();
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL = 1500; // 1.5 seconds between requests

/**
 * Implements exponential backoff retry logic for rate-limited requests
 * @param {Function} fn - The function to retry
 * @param {number} attempt - Current retry attempt number
 * @returns {Promise} Result of the function or throws error
 */
async function retryWithBackoff(fn, attempt = 0) {
  try {
    return await fn();
  } catch (error) {
    // Only retry for rate limit errors
    if (error instanceof RateLimitError && attempt < RATE_LIMIT_CONFIG?.maxRetries) {
      const delay = Math.min(
        RATE_LIMIT_CONFIG?.baseDelay * Math.pow(RATE_LIMIT_CONFIG?.backoffMultiplier, attempt),
        RATE_LIMIT_CONFIG?.maxDelay
      );
      
      console.log(`Rate limit hit. Retrying in ${delay}ms (attempt ${attempt + 1}/${RATE_LIMIT_CONFIG?.maxRetries})...`);
      
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryWithBackoff(fn, attempt + 1);
    }
    
    // For non-rate-limit errors or exhausted retries, throw the error
    throw error;
  }
}

/**
 * Throttles API requests to prevent hitting rate limits
 * @param {Function} fn - The function to throttle
 * @returns {Promise} Result of the throttled function
 */
async function throttleRequest(fn) {
  return new Promise((resolve, reject) => {
    requestQueue = requestQueue.then(async () => {
      // Ensure minimum time between requests
      const now = Date.now();
      const timeSinceLastRequest = now - lastRequestTime;
      
      if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
        const waitTime = MIN_REQUEST_INTERVAL - timeSinceLastRequest;
        await new Promise(r => setTimeout(r, waitTime));
      }
      
      lastRequestTime = Date.now();
      
      try {
        const result = await fn();
        resolve(result);
      } catch (error) {
        reject(error);
      }
    });
  });
}

/**
 * Maps OpenAI API error types to user-friendly error messages.
 * @param {Error} error - The error object from OpenAI API.
 * @returns {Object} Error information with message and internal flag.
 */
function getErrorMessage(error) {
  console.error('OpenAI API error details:', {
    type: error?.constructor?.name,
    message: error?.message,
    status: error?.status,
    code: error?.code,
    cause: error?.cause,
    stack: error?.stack?.substring(0, 200)
  });

  if (error instanceof AuthenticationError) {
    return { isInternal: true, message: 'Ungültiger API-Schlüssel oder Authentifizierung fehlgeschlagen. Bitte überprüfen Sie Ihren OpenAI API-Schlüssel.' };
  } else if (error instanceof PermissionDeniedError) {
    return { isInternal: true, message: 'Kontingent überschritten oder Autorisierung fehlgeschlagen. Möglicherweise haben Sie Ihr Nutzungslimit überschritten.' };
  } else if (error instanceof RateLimitError) {
    return { 
      isInternal: true, 
      message: 'Zu viele Anfragen in kurzer Zeit. Das System versucht es automatisch erneut. Bitte haben Sie einen Moment Geduld.' 
    };
  } else if (error instanceof InternalServerError) {
    return { isInternal: true, message: 'OpenAI-Dienst ist derzeit nicht verfügbar. Bitte versuchen Sie es später erneut.' };
  } else if (error instanceof APIConnectionError) {
    // Enhanced connection error handling with diagnostics
    const diagnostics = [
      'Verbindungsfehler zum OpenAI-Dienst.',
      error?.message ? `Details: ${error?.message}` : null,
      error?.cause ? `Ursache: ${JSON.stringify(error?.cause)}` : null
    ]?.filter(Boolean)?.join(' ');
    
    return { 
      isInternal: true, 
      message: 'Verbindung zum OpenAI-Dienst nicht möglich. Mögliche Ursachen: Netzwerkproblem, CORS-Fehler, oder API-Dienst nicht erreichbar. Bitte versuchen Sie es später erneut.' 
    };
  } else {
    return { isInternal: false, message: error?.message || 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.' };
  }
}

/**
 * Extracts chassis number from vehicle registration PDF using OpenAI Vision API
 * via Supabase Edge Function proxy to avoid CORS issues.
 * @param {File|string} pdfFile - PDF file or data URL to analyze.
 * @returns {Promise<Object>} Extracted chassis number with confidence level and quality metrics.
 */
export async function extractChassisFromPDF(pdfFile) {
  try {
    // Step 1: Perform document quality analysis first (client-side, no API call)
    let qualityAnalysis = null;
    try {
      qualityAnalysis = await analyzeDocumentQuality(pdfFile);
    } catch (qualityError) {
      console.warn('Quality analysis failed, proceeding with OCR:', qualityError);
      // Continue with OCR even if quality analysis fails
    }

    // Step 2: Check if quality is too poor to proceed
    if (qualityAnalysis && qualityAnalysis?.overallScore < 40) {
      return {
        chassisNumber: null,
        confidence: 0,
        location: 'Not extracted due to poor quality',
        alternativeNumbers: [],
        status: 'quality_check_failed',
        qualityAnalysis,
        error: 'Dokumentqualität ist zu schlecht für OCR. Bitte besseres Foto aufnehmen.',
      };
    }

    // Step 3: Convert PDF file to data URL if it's a File object
    let imageUrl;
    
    if (typeof pdfFile === 'string') {
      imageUrl = pdfFile;
    } else if (pdfFile?.url) {
      imageUrl = pdfFile?.url;
    } else if (pdfFile instanceof File) {
      // Convert File to base64 data URL
      imageUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = (event) => {
          // FileReader.onerror receives a ProgressEvent, not an Error object
          // Extract the actual error from the FileReader's error property
          const fileReaderError = event?.target?.error;
          let errorMessage = fileReaderError?.message || fileReaderError?.name || 'Fehler beim Lesen der Datei';
          console.error('FileReader error:', fileReaderError);
          reject(new Error(errorMessage));
        };
        reader.readAsDataURL(pdfFile);
      });
    } else {
      throw new Error('Ungültiges Dateiformat. Bitte geben Sie eine gültige PDF- oder Bilddatei an.');
    }

    // Step 4: Call Supabase Edge Function instead of direct OpenAI API
    console.log('🚀 Calling Supabase Edge Function for chassis extraction...');
    console.log('📊 Image URL length:', imageUrl?.length);
    console.log('📊 Quality analysis:', qualityAnalysis ? 'Available' : 'Not available');

    // Diagnostic logging
    console.log('🔍 Supabase Configuration Check:');
    console.log('  - Base URL:', import.meta.env?.VITE_SUPABASE_URL);
    console.log('  - Expected Function URL:', `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/extract-chassis`);
    console.log('  - Anon Key present:', !!import.meta.env?.VITE_SUPABASE_ANON_KEY);
    
    // Check if user is authenticated
    const { data: sessionData } = await supabase?.auth?.getSession();
    console.log('  - Auth session present:', !!sessionData?.session);
    console.log('  - Access token present:', !!sessionData?.session?.access_token);

    // Use direct fetch to Edge Function with full imageUrl
    const functionUrl = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/extract-chassis`;
    console.log('🚀 Calling Edge Function:', functionUrl);
    
    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json','Authorization': `Bearer ${import.meta.env?.VITE_SUPABASE_ANON_KEY}`,
        'apikey': import.meta.env?.VITE_SUPABASE_ANON_KEY
      },
      body: JSON.stringify({
        imageUrl,
        qualityAnalysis
      })
    });

    if (!response?.ok) {
      const errorText = await response?.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText };
      }
      
      console.error('❌ Edge Function error:', {
        status: response?.status,
        statusText: response?.statusText,
        error: errorData
      });

      // Check for specific error types
      let errorMessage = 'Fehler beim Extrahieren der Fahrgestellnummer.';
      let userFriendlyMessage = '';
      
      if (errorData?.message?.includes('OPENAI_API_KEY')) {
        errorMessage = 'OpenAI API-Schlüssel ist nicht konfiguriert.';
        userFriendlyMessage = 'Die OpenAI-Integration ist nicht korrekt konfiguriert. Bitte kontaktieren Sie den Administrator.';
        console.error('⚠️ CONFIGURATION ERROR: OPENAI_API_KEY secret is not set in Supabase.');
        console.error('📝 To fix: Run "supabase secrets set OPENAI_API_KEY=your-key-here"');
      } else if (errorData?.message?.includes('Failed to send a request') || errorData?.message?.includes('FunctionsFetchError') ||errorData?.message?.includes('Not Found') ||errorData?.message?.includes('404')) {
        errorMessage = 'Die Extraktionsfunktion ist nicht verfügbar.';
        userFriendlyMessage = 'Die Chassis-Extraktionsfunktion konnte nicht erreicht werden. Bitte versuchen Sie es später erneut.';
        console.error('⚠️ DEPLOYMENT ERROR: Edge Function may not be deployed or accessible.');
      } else if (errorData?.message?.includes('401') || errorData?.message?.includes('Unauthorized')) {
        errorMessage = 'Authentifizierungsfehler beim Aufruf der Extraktionsfunktion.';
        userFriendlyMessage = 'Authentifizierungsfehler. Bitte melden Sie sich erneut an.';
      } else if (errorData?.message?.includes('500') || errorData?.message?.includes('Internal Server Error')) {
        errorMessage = 'Interner Serverfehler in der Extraktionsfunktion.';
        userFriendlyMessage = 'Ein Serverfehler ist aufgetreten. Bitte versuchen Sie es später erneut.';
      }
      
      return {
        chassisNumber: null,
        confidence: 0,
        ocrConfidence: 0,
        confidenceDetails: {
          baseConfidence: 0,
          qualityAdjustment: 0,
          finalConfidence: 0,
          factors: ['Extraction failed']
        },
        location: 'Extraction failed',
        alternativeNumbers: [],
        extractionNotes: errorData?.message || errorMessage,
        validationWarnings: [errorData?.message || 'Unknown error'],
        status: 'error',
        qualityAnalysis,
        error: userFriendlyMessage || errorMessage,
        technicalError: errorData?.message
      };
    }

    // Parse successful response
    const data = await response?.json();

    // Log successful response
    console.log('✅ Edge Function response received:', {
      status: data?.status,
      hasChassisNumber: !!data?.chassisNumber,
      confidence: data?.confidence
    });

    if (data?.status === 'error') {
      console.error('❌ Extraction error from Edge Function:', data?.error);
      return {
        chassisNumber: null,
        confidence: 0,
        ocrConfidence: 0,
        confidenceDetails: {
          baseConfidence: 0,
          qualityAdjustment: 0,
          finalConfidence: 0,
          factors: ['Extraction failed']
        },
        location: 'Extraction failed',
        alternativeNumbers: [],
        extractionNotes: data?.error || 'Fehler beim Extrahieren der Fahrgestellnummer',
        validationWarnings: [data?.error || 'Unknown error'],
        status: 'error',qualityAnalysis,error: data?.error || 'Fehler beim Extrahieren der Fahrgestellnummer. Bitte versuchen Sie es erneut.'
      };
    }

    // Step 5: Process the extraction result
    const extractionResult = data;
    
    // Validate chassis number format
    const chassisNumber = extractionResult?.chassisNumber;
    const validationWarnings = extractionResult?.validationWarnings || [];
    
    if (chassisNumber) {
      // VIN validation: Must be exactly 17 characters, alphanumeric, no I, O, Q
      const vinRegex = /^[A-HJ-NPR-Z0-9]{17}$/;
      
      if (!vinRegex?.test(chassisNumber)) {
        validationWarnings?.push('VIN format validation failed: Must be exactly 17 characters (A-H, J-N, P, R-Z, 0-9)');
        
        if (chassisNumber?.length !== 17) {
          validationWarnings?.push(`VIN length is ${chassisNumber?.length}, expected 17`);
        }
        
        if (/[IOQ]/i?.test(chassisNumber)) {
          validationWarnings?.push('VIN contains invalid characters: I, O, or Q');
        }
      }
    }

    // Step 6: Calculate enhanced confidence with quality analysis
    const baseConfidence = extractionResult?.confidence || 0;
    const ocrConfidence = qualityAnalysis 
      ? calculateEnhancedOCRConfidence(qualityAnalysis, baseConfidence)
      : baseConfidence;

    // Step 7: Return comprehensive result
    return {
      chassisNumber,
      confidence: baseConfidence,
      ocrConfidence,
      confidenceDetails: {
        baseConfidence,
        qualityAdjustment: ocrConfidence - baseConfidence,
        finalConfidence: ocrConfidence,
        factors: qualityAnalysis ? [
          `Sharpness: ${qualityAnalysis?.sharpness?.toFixed(1)}`,
          `Contrast: ${qualityAnalysis?.contrast?.toFixed(1)}`,
          `Brightness: ${qualityAnalysis?.brightness?.toFixed(1)}`,
          `Overall Quality: ${qualityAnalysis?.overallScore?.toFixed(1)}`
        ] : ['No quality analysis available']
      },
      location: extractionResult?.location || 'Field E',
      alternativeNumbers: extractionResult?.alternativeNumbers || [],
      extractionNotes: extractionResult?.extractionNotes || '',
      validationWarnings,
      status: chassisNumber ? 'success' : 'not_found',
      qualityAnalysis,
      rawResponse: extractionResult
    };

  } catch (error) {
    console.error('❌ Chassis extraction failed:', error);
    
    return {
      chassisNumber: null,
      confidence: 0,
      ocrConfidence: 0,
      confidenceDetails: {
        baseConfidence: 0,
        qualityAdjustment: 0,
        finalConfidence: 0,
        factors: ['Extraction failed']
      },
      location: 'Extraction failed',
      alternativeNumbers: [],
      extractionNotes: error?.message || 'Ein unerwarteter Fehler ist aufgetreten',
      validationWarnings: [error?.message || 'Unknown error'],
      status: 'error',
      qualityAnalysis: null,
      error: 'Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut.'
    };
  }
}

/**
 * Validates a chassis number format (VIN format).
 * @param {string} chassisNumber - The chassis number to validate.
 * @returns {Object} Validation result with isValid flag and message.
 */
export function validateChassisNumber(chassisNumber) {
  if (!chassisNumber) {
    return { isValid: false, message: 'Chassis number is required' };
  }

  const vinPattern = /^[A-HJ-NPR-Z0-9]{17}$/;
  
  if (chassisNumber?.length !== 17) {
    return { 
      isValid: false, 
      message: `Invalid length: ${chassisNumber?.length} characters (must be exactly 17)` 
    };
  }

  if (!vinPattern?.test(chassisNumber)) {
    return { 
      isValid: false, 
      message: 'Invalid format: VIN must contain only A-Z (excluding I, O, Q) and 0-9' 
    };
  }

  return { isValid: true, message: 'Valid VIN format' };
}

/**
 * Checks if a chassis number already exists in the applications table.
 * @param {string} chassisNumber - The chassis number to check.
 * @param {Object} supabaseClient - Supabase client instance.
 * @returns {Promise<Object>} Result with exists flag and application details if found.
 */
export async function checkChassisNumberExists(chassisNumber, supabaseClient) {
  try {
    if (!chassisNumber || !supabaseClient) {
      return { 
        exists: false, 
        error: 'Chassis number and Supabase client are required' 
      };
    }

    const { data, error } = await supabaseClient
      ?.from('applications')
      ?.select('id, chassis_number, customer_name, customer_email, application_date, application_status')
      ?.eq('chassis_number', chassisNumber?.toUpperCase())
      ?.single();

    if (error) {
      if (error?.code === 'PGRST116') {
        return { exists: false, application: null };
      }
      console.error('Error checking chassis number:', error);
      return { 
        exists: false, 
        error: 'Database error while checking chassis number',
        details: error?.message 
      };
    }

    if (data) {
      return {
        exists: true,
        application: {
          id: data?.id,
          chassisNumber: data?.chassis_number,
          customerName: data?.customer_name,
          customerEmail: data?.customer_email,
          applicationDate: data?.application_date,
          status: data?.application_status
        },
        message: 'Diese Fahrgestellnummer wurde bereits für eine Bewerbung verwendet.'
      };
    }

    return { exists: false, application: null };
  } catch (err) {
    console.error('Unexpected error checking chassis number:', err);
    return { 
      exists: false, 
      error: 'Unexpected error while checking chassis number',
      details: err?.message 
    };
  }
}